import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  Divider,
  Stack,
  Typography,
} from "@datanomik/commons-ui";
import { DialogContent, DialogActions, Fade } from "@mui/material";
import {
  ShieldOutlined,
  VisibilityOffOutlined,
  BuildOutlined,
  RocketLaunchOutlined,
} from "@mui/icons-material";
import { keyframes } from "@emotion/react";
import { isDemo } from "@/config/apiUrls";

const shimmer = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const glow = keyframes`
  0%, 100% {
    box-shadow:
      0 0 12px rgba(99, 102, 241, 0.25),
      0 0 24px rgba(139, 92, 246, 0.15),
      0 4px 16px rgba(40, 69, 210, 0.2);
  }
  50% {
    box-shadow:
      0 0 18px rgba(99, 102, 241, 0.4),
      0 0 36px rgba(139, 92, 246, 0.25),
      0 4px 24px rgba(40, 69, 210, 0.3);
  }
`;

const dotPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.75); }
`;

const features = [
  {
    icon: VisibilityOffOutlined,
    titleKey: "demo_feature_data_title",
    descKey: "demo_feature_data_desc",
  },
  {
    icon: BuildOutlined,
    titleKey: "demo_feature_limited_title",
    descKey: "demo_feature_limited_desc",
  },
  {
    icon: ShieldOutlined,
    titleKey: "demo_feature_safe_title",
    descKey: "demo_feature_safe_desc",
  },
  {
    icon: RocketLaunchOutlined,
    titleKey: "demo_feature_quick_title",
    descKey: "demo_feature_quick_desc",
  },
];

export const DemoBadge: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!isDemo()) return null;

  return (
    <>
      {/* Floating badge */}
      <Box
        onClick={() => setOpen(true)}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 9999,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 2,
          py: 0.75,
          borderRadius: "20px",
          background: "linear-gradient(270deg, #2845D2, #6366f1, #8b5cf6, #a78bfa, #6366f1, #2845D2)",
          backgroundSize: "300% 300%",
          animation: `${shimmer} 6s ease infinite, ${glow} 3s ease-in-out infinite`,
          color: "#fff",
          fontSize: 12.5,
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          transition: "transform 0.2s, box-shadow 0.2s",
          userSelect: "none",
          "&:hover": {
            transform: "scale(1.08)",
            boxShadow:
              "0 0 24px rgba(99, 102, 241, 0.5), 0 0 48px rgba(139, 92, 246, 0.3), 0 6px 28px rgba(40, 69, 210, 0.35)",
          },
          "&:active": {
            transform: "scale(0.97)",
          },
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: "#4ade80",
            flexShrink: 0,
            animation: `${dotPulse} 2s ease-in-out infinite`,
          }}
        />
        Demo
      </Box>

      {/* Info modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #2845D2 0%, #6366f1 50%, #8b5cf6 100%)",
            px: 3.5,
            py: 4,
            textAlign: "center",
          }}
        >
          <Typography
            fontSize={11}
            fontWeight={700}
            letterSpacing="0.12em"
            textTransform="uppercase"
            sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}
          >
            {t("demo_header_label")}
          </Typography>
          <Typography fontSize={22} fontWeight={700} sx={{ color: "#fff" }}>
            {t("demo_header_title")}
          </Typography>
          <Typography fontSize={13} sx={{ color: "rgba(255,255,255,0.8)", mt: 1, lineHeight: 1.6 }}>
            {t("demo_header_subtitle")}
          </Typography>
        </Box>

        <Divider />

        <DialogContent sx={{ px: 3.5, py: 3 }}>
          <Stack spacing={2.5}>
            {features.map(({ icon: Icon, titleKey, descKey }, i) => (
              <Stack key={i} direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "primary.50",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 20, color: "primary.main" }} />
                </Box>
                <Box>
                  <Typography fontSize={13.5} fontWeight={600}>
                    {t(titleKey)}
                  </Typography>
                  <Typography fontSize={12.5} color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.5 }}>
                    {t(descKey)}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3.5, py: 2 }}>
          <Button
            onClick={() => setOpen(false)}
            variant="contained"
            fullWidth
            sx={{
              background: "linear-gradient(135deg, #2845D2, #6366f1)",
              fontWeight: 600,
              py: 1.25,
              "&:hover": {
                background: "linear-gradient(135deg, #1e3ab8, #5558e6)",
              },
            }}
          >
            {t("demo_cta")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
